// ════════════════════════════════════════════════════════════════════════════
//  pages/Fiscal/Averbacao.tsx
//  Histórico de averbação de CT-e e MDF-e junto à seguradora
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Shield, XCircle, Search, ChevronLeft, ChevronRight, RefreshCw, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useAverbacao } from '@/features/fiscal/transporte/hooks/useAverbacao';
import type { AverbarRequest, MovAverbacaoDto } from '@/features/fiscal/transporte/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const PAGE_SIZES = [10, 20, 50] as const;

type StatusAverb = 'averbado' | 'pendente' | 'erro';

const statusConfig: Record<StatusAverb, { label: string; className: string }> = {
  averbado: { label: 'Averbado', className: 'bg-green-500/10 text-green-600'   },
  pendente: { label: 'Pendente', className: 'bg-yellow-500/10 text-yellow-600' },
  erro:     { label: 'Erro',     className: 'bg-red-500/10 text-red-600'       },
};

function resolveStatus(statusAverbacao: string): StatusAverb {
  const s = statusAverbacao?.toLowerCase() ?? '';
  if (s === 'averbado') return 'averbado';
  if (s === 'pendente') return 'pendente';
  return 'erro';
}

const chaveResumida = (chave: string | null) =>
  chave ? `...${chave.slice(-12)}` : '—';

const FORM_AVERBAR = {
  chaveAcesso: '',
  tipoDocumento: 'CTe' as 'CTe' | 'MDFe',
  xmlConteudo: '',
  valorCarga: 0,
  numeroCte: 0,
  serieCte: '',
  empresaId: 1,
};

const Averbacao = () => {
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [busca,         setBusca]         = useState('');
  const [expandedId,    setExpandedId]    = useState<number | null>(null);

  // Dialogs
  const [averbarOpen,   setAverbarOpen]   = useState(false);
  const [cancelandoDoc, setCanceladoDoc]  = useState<MovAverbacaoDto | null>(null);
  const [motivoCancel,  setMotivoCancel]  = useState('');

  // Form averbar
  const [form, setForm] = useState<typeof FORM_AVERBAR>(FORM_AVERBAR);

  const { itens, isLoading, averbar, cancelar } = useAverbacao(pagina, tamanhoPagina);

  const itensFiltrados = itens.filter((i) => {
    const q = busca.trim().toLowerCase();
    return !q
      || String(i.numeroCte).includes(q)
      || (i.chaveCte ?? '').toLowerCase().includes(q)
      || (i.chaveMdfe ?? '').toLowerCase().includes(q)
      || (i.descricaoSeguradora ?? '').toLowerCase().includes(q);
  });

  const totalAverbados = itens.filter((i) => resolveStatus(i.statusAverbacao) === 'averbado').length;
  const totalPendentes = itens.filter((i) => resolveStatus(i.statusAverbacao) === 'pendente').length;
  const totalErros     = itens.filter((i) => resolveStatus(i.statusAverbacao) === 'erro').length;

  const total       = itens.length;
  const firstItem   = (pagina - 1) * tamanhoPagina + 1;
  const lastItem    = Math.min(pagina * tamanhoPagina, total);
  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));

  const setF = <K extends keyof typeof FORM_AVERBAR>(k: K, v: (typeof FORM_AVERBAR)[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const handleAverbar = async () => {
    await averbar.mutateAsync(form as AverbarRequest);
    setAverbarOpen(false);
    setForm(FORM_AVERBAR);
  };

  const handleCancelar = () => {
    if (!cancelandoDoc) return;
    cancelar.mutate({ id: cancelandoDoc.id, motivo: motivoCancel });
    setCanceladoDoc(null);
    setMotivoCancel('');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Averbação</h1>
          <p className="text-muted-foreground mt-1">Averbação de CT-e e MDF-e junto à seguradora</p>
        </div>
        <Button className="gap-2" onClick={() => { setForm(FORM_AVERBAR); setAverbarOpen(true); }}>
          <Plus className="h-4 w-4" />
          Averbar Manualmente
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Averbados', value: totalAverbados, color: 'text-green-500'  },
          { label: 'Pendentes', value: totalPendentes, color: 'text-yellow-500' },
          { label: 'Erros',     value: totalErros,     color: 'text-red-500'    },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <p className="text-xs text-muted-foreground mt-1">Nesta página</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle>Histórico de Averbações</CardTitle>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Número, chave ou seguradora..."
                value={busca}
                onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº CT-e</TableHead>
                  <TableHead>Série</TableHead>
                  <TableHead>Chave CT-e / MDF-e</TableHead>
                  <TableHead>Seguradora</TableHead>
                  <TableHead>Data Averbação</TableHead>
                  <TableHead className="text-right">Valor Carga</TableHead>
                  <TableHead className="text-center">Tentativas</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : itensFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Nenhuma averbação encontrada</TableCell>
                  </TableRow>
                ) : (
                  itensFiltrados.map((item) => {
                    const status = resolveStatus(item.statusAverbacao);
                    const cfg    = statusConfig[status];
                    const isExpanded = expandedId === item.id;
                    return (
                      <>
                        <TableRow key={item.id} className={isExpanded ? 'bg-muted/40' : undefined}>
                          <TableCell className="font-medium">{String(item.numeroCte).padStart(6, '0')}</TableCell>
                          <TableCell>{item.serieCte ?? '—'}</TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground"
                            title={(item.chaveCte ?? item.chaveMdfe) ?? undefined}>
                            {chaveResumida(item.chaveCte ?? item.chaveMdfe)}
                          </TableCell>
                          <TableCell className="max-w-[140px] truncate">{item.descricaoSeguradora ?? '—'}</TableCell>
                          <TableCell>{new Date(item.dtAverbacao).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell className="text-right">
                            {item.valorCarga.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </TableCell>
                          <TableCell className="text-center">{item.tentativasEnvio}</TableCell>
                          <TableCell>
                            <Badge className={cfg.className}>{cfg.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7"
                                onClick={() => setExpandedId(isExpanded ? null : item.id)}
                                title="Ver detalhes">
                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                              </Button>
                              {!item.isCancelado && (
                                <Button size="icon" variant="ghost"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => { setCanceladoDoc(item); setMotivoCancel(''); }}
                                  title="Cancelar averbação">
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${item.id}-detail`} className="bg-muted/20">
                            <TableCell colSpan={9} className="py-3 px-6">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Protocolo</p>
                                  <p className="font-mono">{item.protocolo ?? '—'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Tentativas de Envio</p>
                                  <p>{item.tentativasEnvio}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Última Tentativa</p>
                                  <p>{item.dtUltimaTentativa ? new Date(item.dtUltimaTentativa).toLocaleString('pt-BR') : '—'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground text-xs font-medium mb-1">Valor Averbação</p>
                                  <p>{item.valorAverbacao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={(v) => { setTamanhoPagina(Number(v)); setPagina(1); }}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)          page = i + 1;
                  else if (pagina <= 3)                 page = i + 1;
                  else if (pagina >= totalPaginas - 2)  page = totalPaginas - 4 + i;
                  else                                  page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog: Averbar Manualmente */}
      <Dialog open={averbarOpen} onOpenChange={(o) => { if (!o) setAverbarOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Averbar Manualmente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Chave de Acesso * (44 dígitos)</Label>
              <Input maxLength={44} value={form.chaveAcesso} onChange={(e) => setF('chaveAcesso', e.target.value)}
                placeholder="44 dígitos" />
            </div>
            <div className="space-y-1">
              <Label>Tipo de Documento *</Label>
              <Select value={form.tipoDocumento} onValueChange={(v) => setF('tipoDocumento', v as 'CTe' | 'MDFe')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CTe">CT-e</SelectItem>
                  <SelectItem value="MDFe">MDF-e</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Número CT-e *</Label>
                <Input type="number" min={0} value={form.numeroCte}
                  onChange={(e) => setF('numeroCte', Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label>Série CT-e</Label>
                <Input value={form.serieCte} onChange={(e) => setF('serieCte', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Valor da Carga (R$) *</Label>
                <Input type="number" min={0} step={0.01} value={form.valorCarga}
                  onChange={(e) => setF('valorCarga', Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label>ID Empresa *</Label>
                <Input type="number" min={1} value={form.empresaId}
                  onChange={(e) => setF('empresaId', Number(e.target.value))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAverbarOpen(false)}>Cancelar</Button>
            <Button onClick={handleAverbar}
              disabled={averbar.isPending || !form.chaveAcesso || form.numeroCte <= 0}>
              {averbar.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Averbando...</>
              ) : (
                <><Shield className="h-4 w-4 mr-2" />Averbar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Cancelar Averbação */}
      <Dialog open={!!cancelandoDoc} onOpenChange={(o) => { if (!o) setCanceladoDoc(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar Averbação — CT-e {cancelandoDoc ? String(cancelandoDoc.numeroCte).padStart(6, '0') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Motivo do cancelamento</Label>
              <Input value={motivoCancel} onChange={(e) => setMotivoCancel(e.target.value)}
                placeholder="Descreva o motivo..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCanceladoDoc(null)}>Fechar</Button>
            <Button variant="destructive" onClick={handleCancelar} disabled={cancelar.isPending}>
              {cancelar.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cancelando...</>
              ) : (
                <><XCircle className="h-4 w-4 mr-2" />Confirmar Cancelamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Averbacao;
